import React, { useMemo } from "react";
import { TableHeader, WithRoleAccess } from "@services/ui-components";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import RoleManagementTable from "./RoleManagementTable/RoleManagementTable";
import { useToast } from "../../../hooks/useToast";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import useRoleManagement from "./hooks/useRoleManagement";
import RoleManagementContext from "./context/RoleManagementContext";
import { SimpleModal } from "@services/ui-components/src/SimpleModal/SimpleModal";
import Button from "react-bootstrap/Button";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { cloneNameToDisplay } from "../../Me-menu/utils/cloneNameUtils";
import RoleManagementInfoBanner from "./RoleManagementInfoBanner";

const RoleManagementComponent: React.FC = () => {
    const { t: translate } = useTranslation();
    const { branchId } = useParams();
    const { showToast, setShowToast, toggleShowToast, toastText } = useToast();
    const canDeleteRole = useRoleAccess(CompanyAccess.RoleDelete);
    const canCloneRole = useRoleAccess(CompanyAccess.RoleCreateClone);

    const {
        onDeleteRole,
        deleteTarget,
        setDeleteTarget,
        onSubmitDeleteHandler,
        cloneTarget,
        setCloneTarget,
        onSubmitCloneHandler,
        onCloneRole,
        roleData,
        roleDataLoading,
        refetchRole,
        currentPage,
        pageCount,
        totalItems,
        firstItemIndex,
        lastItemIndex,
        handlePageChange,
    } = useRoleManagement();

    const delModalInfo = useMemo(() => {
        if (deleteTarget) {
            if (!deleteTarget.isUsableRole) {
                return {
                    title: translate("Delete role"),
                    desc: translate("Delete role confirmation", {
                        roleName: deleteTarget?.roleName,
                    }),
                };
            }
            if (deleteTarget.isUsableRole) {
                return {
                    title: translate("Delete role unavailable"),
                    desc: translate("Delete role unavailable description", {
                        roleName: deleteTarget?.roleName,
                    }),
                };
            }
        }

        return null;
    }, [deleteTarget, translate]);

    return (
        <div className={"mb-5"}>
            <RoleManagementInfoBanner variant="list" />

            <TableHeader
                title={translate("Role Management")}
                paths={[
                    {
                        path: `/branch/access-settings/${branchId}`,
                        label: translate("Access settings"),
                    },
                    {
                        path: ``,
                        label: translate("Role Management"),
                    },
                ]}
                colSize={5}
                btnColSize={7}
                buttons={
                    <Row className="gx-3 justify-content-end mt-4 mb-2">
                        <Col xs={12} sm={12} lg={5} xl={3} xxl={4}>
                            <WithRoleAccess permission={CompanyAccess.RoleCreateClone}>
                                <Link
                                    to="roles/create"
                                    className="btn btn-primary w-100 fw-bold px-2 text-truncate d-inline-block text-center"
                                >
                                    <i className="bi bi-plus-lg me-2"></i>
                                    {translate("Create role")}
                                </Link>
                            </WithRoleAccess>
                        </Col>
                    </Row>
                }
            />

            <RoleManagementContext.Provider
                value={{
                    onDeleteRole,
                    deleteTarget,
                    setDeleteTarget,
                    onCloneRole,
                    roleData,
                    roleDataLoading,
                    refetchRole,
                    currentPage,
                    pageCount,
                    totalItems,
                    firstItemIndex,
                    lastItemIndex,
                    handlePageChange,
                }}
            >
                <RoleManagementTable />
            </RoleManagementContext.Provider>

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={translate(toastText)} />}
            <SimpleModal
                isOpen={Boolean(cloneTarget)}
                onClose={() => setCloneTarget(null)}
                title={translate("Clone role")}
                description={translate("Clone role confirmation", {
                    roleName: cloneNameToDisplay(cloneTarget?.roleName, translate),
                })}
            >
                <Container>
                    <Row>
                        <Col xs={6} className={"ps-0"}>
                            <Button
                                variant="outline-primary"
                                className={"w-100 fw-normal"}
                                onClick={() => setCloneTarget(null)}
                            >
                                {translate("Cancel")}
                            </Button>
                        </Col>
                        <Col xs={6} className={"pe-0"}>
                            <Button
                                variant="primary"
                                className={"w-100 fw-normal"}
                                disabled={!canCloneRole}
                                onClick={async () => {
                                    if (!canCloneRole || !cloneTarget) return;
                                    await onSubmitCloneHandler(cloneTarget);
                                }}
                            >
                                {translate("Clone")}
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </SimpleModal>
            <SimpleModal
                isOpen={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                title={delModalInfo?.title}
                description={delModalInfo?.desc}
            >
                <Container>
                    <Row>
                        <Col xs={6} className={"ps-0"}>
                            <Button
                                variant="outline-primary"
                                className={"w-100 fw-normal"}
                                onClick={() => setDeleteTarget(null)}
                            >
                                {translate("Cancel")}
                            </Button>
                        </Col>
                        <Col xs={6} className={"pe-0"}>
                            {!deleteTarget?.isUsableRole ? (
                                <Button
                                    variant="primary"
                                    className={"w-100 fw-normal"}
                                    disabled={!canDeleteRole}
                                    onClick={async () => {
                                        if (!canDeleteRole) return;
                                        if (!deleteTarget?.roleId) return;
                                        await onSubmitDeleteHandler(deleteTarget.roleId);
                                        toggleShowToast("Deleted");
                                    }}
                                >
                                    {translate("Delete")}
                                </Button>
                            ) : (
                                <Link
                                    to={`/employees-list/${branchId}`}
                                    state={{ roleId: deleteTarget?.roleId }}
                                    className="btn btn-primary w-100 fw-normal text-center"
                                >
                                    {translate("List of employees")}
                                </Link>
                            )}
                        </Col>
                    </Row>
                </Container>
            </SimpleModal>
        </div>
    );
};

export default RoleManagementComponent;
